import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Loader2, Search, Gem, Lock, Unlock, ScrollText, X } from 'lucide-react';
import { fmt, fmtDate, gbErr } from './gbAdminUtils';

const LEDGER_LABEL = {
  recharge: 'Diamantes por compra', mission_entry: 'Entrada de fase',
  mission_reward: 'Prêmio de fase', upgrade: 'Melhoria', item: 'Item',
  welcome: 'Boas-vindas', admin_adjust: 'Ajuste do admin',
};

const AdjustForm = ({ seasonId, player, onDone, onClose }) => {
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!Number(amount)) { toast.error('Informe um valor diferente de zero.'); return; }
    if (reason.trim().length < 3) { toast.error('Escreva o motivo do ajuste.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/gb/season/${seasonId}/adjust`,
        { kako_id: player.kako_id, diamonds: Number(amount), reason: reason.trim() });
      toast.success(`Novo saldo: ${fmt(data.balance)} 💎`);
      onClose(); await onDone();
    } catch (e) { toast.error(gbErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg border border-cyan-200 bg-cyan-50 p-2">
      <label className="text-xs font-bold text-slate-600">
        <span className="block">Diamantes (use − para retirar)</span>
        <input type="number" value={amount} data-testid="gb-adjust-amount"
          onChange={(e) => setAmount(e.target.value)} className="mt-1 h-9 w-28 rounded-lg border px-2 text-sm font-mono" />
      </label>
      <label className="min-w-[200px] flex-1 text-xs font-bold text-slate-600">
        <span className="block">Motivo</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="gb-adjust-reason"
          className="mt-1 h-9 w-full rounded-lg border px-2 text-sm" placeholder="Ex.: compensação por falha na partida" />
      </label>
      <Button size="sm" disabled={busy} onClick={run} data-testid="gb-adjust-confirm"
        className="bg-cyan-600 text-white hover:bg-cyan-700">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Aplicar ajuste'}
      </Button>
      <Button size="sm" variant="outline" onClick={onClose}><X className="h-3.5 w-3.5" /></Button>
    </div>
  );
};

export default function GbPlayers({ seasonId, suspectsOnly = false, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [adjusting, setAdjusting] = useState(null);
  const [ledgerOf, setLedgerOf] = useState(null);
  const [ledger, setLedger] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/gb/season/${seasonId}/players`,
        { params: { suspects: suspectsOnly, limit: 300 } });
      setRows(data.players || []);
    } catch (e) { toast.error(gbErr(e)); } finally { setLoading(false); }
  }, [seasonId, suspectsOnly]);

  useEffect(() => { load(); }, [load]);

  const openLedger = async (kakoId) => {
    if (ledgerOf === kakoId) { setLedgerOf(null); return; }
    setLedgerOf(kakoId); setLedger([]);
    try {
      const { data } = await api.get(`/admin/gb/season/${seasonId}/ledger`,
        { params: { kako_id: kakoId, limit: 60 } });
      setLedger(data.entries || []);
    } catch (e) { toast.error(gbErr(e)); }
  };

  const toggleBlock = async (p) => {
    const motivo = window.prompt(p.blocked
      ? `Motivo para LIBERAR ${p.kako_id}:` : `Motivo para BLOQUEAR ${p.kako_id} no evento:`);
    if (!motivo || motivo.trim().length < 3) return;
    try {
      await api.post(`/admin/gb/season/${seasonId}/block`,
        { kako_id: p.kako_id, blocked: !p.blocked, reason: motivo.trim() });
      toast.success(p.blocked ? 'Jogador liberado.' : 'Jogador bloqueado.');
      await load(); await onChanged?.();
    } catch (e) { toast.error(gbErr(e)); }
  };

  const clearSuspicion = async (p) => {
    const motivo = window.prompt(`Motivo para zerar a suspeita de ${p.kako_id}:`);
    if (!motivo || motivo.trim().length < 3) return;
    try {
      await api.post(`/admin/gb/season/${seasonId}/clear-suspicion`,
        { kako_id: p.kako_id, blocked: false, reason: motivo.trim() });
      toast.success('Suspeita zerada.');
      await load(); await onChanged?.();
    } catch (e) { toast.error(gbErr(e)); }
  };

  const filtered = rows.filter((p) => !q
    || String(p.kako_id).includes(q) || (p.nickname || '').toLowerCase().includes(q.toLowerCase()));

  if (loading) return <div className="flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-3" data-testid={suspectsOnly ? 'gb-admin-suspects' : 'gb-admin-players'}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="gb-players-search"
            placeholder="Buscar por KAKO ID ou apelido"
            className="h-9 w-full rounded-lg border pl-8 pr-2 text-sm" />
        </div>
        <span className="text-xs font-bold text-slate-500">{filtered.length} jogadores</span>
      </div>

      {filtered.length === 0 && (
        <p className="rounded-xl border p-4 text-center text-xs text-slate-400">
          {suspectsOnly ? 'Nenhuma conta suspeita — está tudo limpo.' : 'Nenhum jogador entrou no evento ainda.'}
        </p>
      )}

      {filtered.map((p) => (
        <div key={p.kako_id} className="rounded-xl border p-3" data-testid={`gb-player-${p.kako_id}`}>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <b className="text-slate-700">{p.nickname || '—'}</b>
            <span className="text-slate-400">KAKO {p.kako_id}</span>
            <span className="rounded-full bg-cyan-50 px-2 py-0.5 font-black text-cyan-700">💎 {fmt(p.diamonds)}</span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-bold text-slate-600">
              fase {p.progress?.node || 1} · {(p.progress?.cleared || []).length} limpas
            </span>
            <span className="text-slate-500">
              {p.stats?.mission_wins || 0}V / {p.stats?.mission_losses || 0}D
            </span>
            {p.suspicion > 0 && (
              <span className={`rounded-full px-2 py-0.5 font-black ${
                p.suspicion >= 3 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                suspeita {p.suspicion}
              </span>
            )}
            {p.blocked && <span className="rounded-full bg-rose-600 px-2 py-0.5 font-black text-white">BLOQUEADO</span>}
            <span className="ml-auto flex gap-1">
              <Button size="sm" variant="outline" onClick={() => setAdjusting(adjusting === p.kako_id ? null : p.kako_id)}
                data-testid={`gb-adjust-${p.kako_id}`}>
                <Gem className="mr-1 h-3.5 w-3.5" /> Ajustar
              </Button>
              <Button size="sm" variant="outline" onClick={() => openLedger(p.kako_id)}
                data-testid={`gb-ledger-${p.kako_id}`}>
                <ScrollText className="mr-1 h-3.5 w-3.5" /> Extrato
              </Button>
              {p.suspicion > 0 && (
                <Button size="sm" variant="outline" onClick={() => clearSuspicion(p)}
                  data-testid={`gb-clear-suspicion-${p.kako_id}`}>Zerar suspeita</Button>
              )}
              <Button size="sm" onClick={() => toggleBlock(p)} data-testid={`gb-block-${p.kako_id}`}
                className={p.blocked ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-rose-600 text-white hover:bg-rose-700'}>
                {p.blocked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
              </Button>
            </span>
          </div>

          {p.blocked && p.block_reason && (
            <p className="mt-1 text-[11px] text-rose-600">Motivo do bloqueio: {p.block_reason}</p>
          )}
          <p className="mt-1 text-[10px] text-slate-400">
            Hoje: {p.daily?.missions || 0} partidas · {p.daily?.earned || 0} 💎 ganhos · entrou em {fmtDate(p.created_at)}
          </p>

          {adjusting === p.kako_id && (
            <AdjustForm seasonId={seasonId} player={p} onClose={() => setAdjusting(null)}
              onDone={async () => { await load(); await onChanged?.(); }} />
          )}

          {ledgerOf === p.kako_id && (
            <div className="mt-2 max-h-64 space-y-1 overflow-y-auto rounded-lg border bg-slate-50 p-2">
              {ledger.length === 0 && <p className="text-[11px] text-slate-400">Sem movimentações.</p>}
              {ledger.map((e, i) => (
                <div key={i} className="flex items-center justify-between text-[11px]">
                  <span className="text-slate-600">
                    {LEDGER_LABEL[e.entry_type] || e.entry_type}
                    {e.metadata?.node ? ` · fase ${e.metadata.node}` : ''}
                    {e.metadata?.reason ? ` · ${e.metadata.reason}` : ''}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="text-slate-400">{fmtDate(e.created_at)}</span>
                    <b className={e.amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                      {e.amount >= 0 ? '+' : ''}{fmt(e.amount)}
                    </b>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
